import { Box, ButtonBase, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { startOfWeek, endOfWeek, format, parse, eachDayOfInterval } from "date-fns";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';

import cx from 'classnames';
import { useDispatch, useSelector } from "react-redux";
import Shbutton from "../../theme/buttons/Shbutton";
import { InputDropdown } from "../../theme/dropdowns/InputDropdown";
import NumberInput from "../../theme/inputs/NumberInput";
import ShDateRangePicker from "../../theme/ShDateRangePicker";
import SidepanelLoader from "../../theme/sidepanel2/SidepanelLoader";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import { useTopbar } from "../../../contexts/TopbarProvider";
import { useGetBiDetails2Query, useSaveBusinessIntelligenceReportMutation } from "../../../newapi/global/businessIntelligence";
import { datagridPageSizeOptions } from "../../../newapi/global/filter/DataGridGeneralOptions";
import { tokens } from "../../../theme";
import { showToastMessagePostRequest, showToastMessageRequestError } from "../../../utils/toasts";
import { calculateDaysBetween, chartDateOptions, chartDateTimeFrames, chartRollingUnits, chartTypes, normalizeFrame, pivotTableCategoryOptions, reportFilterComponentTypes, rollingUnitToFrame } from "./chartFilterEnums";
import { CatogoryDropdown } from "./components/category/CatogoryDropdown";
import { RhChart } from "./components/RhChart";
import { RhDataTable } from "./components/RhDataTable";
import { RhFilter } from "./components/RhFilter";
import { useReportHubFilters } from "./ReportHubFilterContext";
import { getReportForEnvironment, getReportKey, getReportLabelKey, getReportsForEnvironment, getSourceType, reporthubChartOptions, reportTypes } from "./reports";
import { getPivotView, purgeNonSerializable } from "./reportUtils";
import { RhCategoryBoolean } from "./components/category/RhCategoryBoolean";
import ShRhDataGridSelectedActions from "../../theme/rhdatagrid/ShRhDataGridSelectedActions";
import DropdownButton from "../../theme/buttons/DropdownButton";
import { toast } from "react-toastify";
import { CategorySearchQueryDropdown } from "./components/category/CategorySearchQueryDropdown";
import { CategoryCountryDropdown } from "./components/category/CategoryCountryDropdown";
import { reportSubtypeKeys, reportTypeOptionKeys } from "./chartOptions";
import { RhPivotTableRow } from "./components/pivottable/RhPivotTableRow";
import { RhPivotTableColumn } from "./components/pivottable/RhPivotTableColumn";
import { RhPivotTable } from "./components/RhPivotTable";
import { RhPivotHeader } from "./components/RhPivotHeader";
import { RhPivotExportButton } from "./components/pivottable/RhPivotExportButton";
import { RhDateSelector, nativeSpinnerSuppression } from "./components/RhDateSelector";
import { RhSubtypeToggle } from "./components/RhSubtypeToggle";
import { RhRollingUnitDropDown } from "./components/RhTimeframe";
import { AnimatePresence } from "framer-motion";
// import { useGetRelationDashboardProductsQuery } from "../../../newapi/warehouse/productSlice";
const SpStockDetails = SidepanelLoader(() => import("../Sidepanels/SpStockDetails/SPStockDetails"));
// global/Sidepanels/SpStockDetails/SPStockDetails


const ReportDetailPageBuilder = () => {
    
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();
    const { showExtraComponent, clearExtraComponent,showExtraTitle,clearExtraTitle } = useTopbar();

    const [page,setPage] = useState(1);
    const [pageSize,setPageSize] = useState(datagridPageSizeOptions[0]);
    const [sortModel, setSortModel] = useState(null);

    const handleSortChange = (newSortModel) => {
        setSortModel(newSortModel);
        setPage(1);
    };

    const filtersRegistry = useReportHubFilters();

    //here i have access to currentreport
    const [currentReport, setCurrentReport] = useState(null);
    const [chartToBuild, setChartToBuild] = useState(null);
    const [chartReadyToPreview,setChartReadyToPreview] = useState(false);

    const availableChartOptions = getReportsForEnvironment(filtersRegistry.environment);

    const chartOption = getReportForEnvironment(getReportKey(currentReport), filtersRegistry.environment);

    // A dimension a report owns is fixed, so its values are the whole set rather than a selection.
    // The registry entry is the one place that knows what that set is.
    const getCategoryValues = (key) => {
        const filterConfig = typeof filtersRegistry.filters[key] === "function"
            ? filtersRegistry.filters[key]()
            : filtersRegistry.filters[key];
        return filterConfig?.getData?.() ?? [];
    };

    // A report that owns its column dimension keeps it however the config was reached — picked fresh,
    // swapped from another report, or opened from a saved one. Seeding it only where the report type is
    // chosen misses every other path, and a column left at type 'column' with no key renders as a date
    // range, which is exactly the wrong thing for a report whose columns carry its meaning.
    useEffect(() => {
        const fixedColumn = chartOption?.fixedPivotColumn;
        if (!fixedColumn || !currentReport?.table) return;

        const column = currentReport.table.column;
        if (column?.type === pivotTableCategoryOptions.COLUMN && column?.key === fixedColumn) return;

        setCurrentReport(prev => ({
            ...prev,
            table: {
                ...prev.table,
                column: {
                    type: pivotTableCategoryOptions.COLUMN,
                    key: fixedColumn,
                    values: [],
                },
            },
        }));
    }, [chartOption?.fixedPivotColumn, currentReport?.table?.column?.type, currentReport?.table?.column?.key]);

    // The chart twin of the effect above. On a chart the owned dimension is the category, and leaving it
    // off or on something else turns the report into a plain shipment count — the one thing it is not.
    // Reasserting it here is also what makes it unsettable: whatever clears it is undone on the next render.
    useEffect(() => {
        const fixedColumn = chartOption?.fixedPivotColumn;
        if (!fixedColumn || currentReport?.reportType !== reportTypeOptionKeys.WITHCHART) return;
        // A category splits bars. The line and area charts plot a date axis and have no second
        // dimension to give it, so asking for one there returns data they cannot draw.
        if (currentReport?.displayType !== chartTypes.BAR) return;

        const category = currentReport.category;
        if (category?.use === true && category?.key === fixedColumn && category?.values?.length > 0) return;

        setCurrentReport(prev => ({
            ...prev,
            category: {
                ...prev.category,
                use: true,
                key: fixedColumn,
                values: getCategoryValues(fixedColumn),
            },
        }));
    }, [chartOption?.fixedPivotColumn, currentReport?.reportType, currentReport?.displayType, currentReport?.category?.use, currentReport?.category?.key, currentReport?.category?.values?.length]);

    const [saveReport, {isLoading:reportSaving}] = useSaveBusinessIntelligenceReportMutation();


    // An `all` category names no values on purpose — the backend resolves them per environment — so an
    // empty list is only a half-built report when the flag is off.
    const categoryInvalid =
    currentReport?.category?.use === true &&
    currentReport?.category?.all !== true &&
    (!currentReport?.category?.values || currentReport.category.values.length === 0);

    const filterInvalid = Object.entries(currentReport?.filter || {}).filter(
        ([key, f]) => Array.isArray(f.value) && f.value.length === 0
    );


    // track previous display type
    const prevDisplayTypeRef = useRef(currentReport?.displayType);

    // update the ref for next render
    useEffect(() => {
        prevDisplayTypeRef.current = currentReport?.displayType;
    }, [currentReport?.displayType]);

    const chartNotReady = !chartReadyToPreview;

    // Skip data fetch for invalid states or when chart isn't ready
    // Name and displayType changes should NOT skip the query - they're purely metadata/visual
    const shouldSkip = chartNotReady || categoryInvalid || filterInvalid.length > 0;

    // Create a query body that excludes displayType and name (purely visual/metadata, doesn't affect data)
    // Create a stable string representation of data-affecting config for comparison
    // This triggers page reset when filters, category, or date range changes
    // Does NOT trigger on: displayType, name, date.frame changes
    const dataAffectingConfigString = useMemo(() => {
        if (!currentReport) return null;
        return JSON.stringify({
            type: currentReport.type,
            // Date changes that affect data (not frame which is just display granularity)
            dateType: currentReport.date?.type,
            dateDays: currentReport.date?.days,
            dateFrom: currentReport.date?.from,
            dateTo: currentReport.date?.to,
            // Category changes
            categoryUse: currentReport.category?.use,
            categoryKey: currentReport.category?.key,
            categoryAll: currentReport.category?.all,
            categoryValues: currentReport.category?.values,
            // All filter changes - stringify to catch nested value changes
            filter: currentReport.filter,
            // What the report counts (entity / lines / products)
            subtype: currentReport.subtype,
        });
    }, [currentReport]);

    // Track previous config string to detect changes
    const prevDataAffectingConfigStringRef = useRef(null);

    // Reset page to 1 when data-affecting config changes (but not on initial load)
    useEffect(() => {
        if (prevDataAffectingConfigStringRef.current !== null && dataAffectingConfigString !== null) {
            if (prevDataAffectingConfigStringRef.current !== dataAffectingConfigString) {
                setPage(1);
            }
        }
        prevDataAffectingConfigStringRef.current = dataAffectingConfigString;
    }, [dataAffectingConfigString]);

    const queryBody = useMemo(() => {
        if (!currentReport) return null;
        const { displayType, name, ...reportWithoutMetadata } = currentReport;
        return purgeNonSerializable(reportWithoutMetadata);
    }, [currentReport?.type, currentReport?.date, currentReport?.category, currentReport?.table, currentReport?.filter, currentReport?.subtype]);

    const queryArgs = useMemo(() => ({
        args: {
            page,
            size: pageSize,
            body: queryBody,
            sortBy: sortModel?.field || null,
            sortDirection: sortModel?.type || null,
        },
    }), [page, pageSize, queryBody, sortModel?.field, sortModel?.type]);

    const { data: biData, isLoading, isSuccess, isFetching: fetchFetching, isError,refetch } = useGetBiDetails2Query(
        queryArgs,
        {
            skip: shouldSkip,
        }
    );

    const [chartInfo,setChartInfo] = useState(null);
    const [data,setData]=useState(null);

    // Track whether the chart is loading (only on page 1, not during pagination)
    const [chartLoading, setChartLoading] = useState(false);

    // Update chart loading state only when fetching page 1
    useEffect(() => {
        if (page === 1) {
            setChartLoading(isLoading || fetchFetching);
        }
    }, [page, isLoading, fetchFetching]);

    useEffect(() => {
        showExtraTitle(
            <Box
                sx={{
                    display:'flex',
                    alignItems:"center",
                    gap:1,
                    color:colors.txt["secondary"]
                }}
            >
                {t("reporthub")}&nbsp;
                /
                <Subheading>
                    {t(getReportLabelKey(getReportKey(currentReport)))}
                </Subheading>
                {/* {chartToBuild !== null && <>&nbsp;&nbsp;&nbsp;/&nbsp;</>}
                {chartToBuild !== null &&
                    <InputDropdown
                        onChange={(selectedIndex) => {
                            const selectedReport = reporthubChartOptions[selectedIndex].name;
                            dispatch(setReport(selectedReport)); 
                        }}
                        options={reporthubChartOptions.map(option => t(option.name))}
                        height={32}
                        variant="darker"
                        selected={reporthubChartOptions.findIndex(option => option.name === currentReport?.type) ?? -1} 
                    />
                } */}
            </Box>
        )

        return () => {
            clearExtraTitle();
            // clearExtraComponent();
        };
    }, [t,i18n.language,chartToBuild,currentReport?.type,currentReport?.reportKey])
    
    useEffect(() => {
        if(!fetchFetching && !isLoading && biData) {
            // Only update chart info on page 1 - subsequent pages don't return new chart data
            if(biData.chart && page === 1) {
                setChartInfo(biData.chart);
            }
            setData(biData.data);
        }
    },[biData,fetchFetching,isLoading, page])

    useEffect(() => {
        if(currentReport !== null && prevDisplayTypeRef?.current) {
            if(prevDisplayTypeRef.current !== currentReport?.displayType) {
                setChartInfo(prev => ({
                    ...prev,
                    displayType:currentReport?.displayType
                }))
            }
        }
    },[currentReport, prevDisplayTypeRef])

    const [nameError,setNameError] = useState(false);
    const [pivotRowError,setPivotRowError] = useState("");
    const heatmapMode = getPivotView(currentReport).heatmap;

    const handleSaveReport =() => {
        if(reportSaving) return;
        if(currentReport.name.length === 0) {
            setNameError(true);
            toast.error(t("name_not_filled_in"), {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }
        let reportToSave = {...currentReport};
        let categorySettings = filtersRegistry.chartSettings?.categorySettings?.();

        if(categorySettings?.defaultDateTime) {
            reportToSave.date = {
                type: categorySettings.defaultDateTime.type,
                frame: categorySettings.defaultDateTime.frame,
                ...(categorySettings.defaultDateTime.days && {days: categorySettings.defaultDateTime.days}),
                ...(categorySettings.defaultDateTime.to && {to: categorySettings.defaultDateTime.to}),
                ...(categorySettings.defaultDateTime.from && {from: categorySettings.defaultDateTime.from}),
            };
        }
        saveReport({name:reportToSave?.name,config:reportToSave}).unwrap().then(res => {
            showToastMessagePostRequest(t,res);
        }).catch(err => {
            console.error(err);
            showToastMessageRequestError(t("create_failed"),t,err);
        })
    }
    
    const collapsed = useSelector((state) => state.settings.environment.collapsed);
    

    return (
        <Box 
            className={cx(undefined, {
                "chart-to-build": chartReadyToPreview === false,
            })}
            sx={{
                background:colors.bg['tertiary'], 
                width:1,
                paddingTop:9,
                "&.chart-to-build" : {
                    pt:12,
                    display:'flex',
                    alignItems:'flex-start',
                    justifyContent:'center',
                }
            }}
        >
            {chartReadyToPreview === false ?
                <Box
                    sx={{
                        mx:3,
                        width:600,
                    }}
                >
                    <Box
                        sx={{
                            p:3,
                            background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                            borderRadius:4,
                            width:1,
                        }}
                    >
                        <Subheading>
                            {t("new_report")}
                        </Subheading>
                        <Box
                            sx={{
                                mt:1.5,
                            }}
                        >
                            <Text>
                                {t("choose_report_type")}
                            </Text>
                            <InputDropdown
                                onChange={(selectedIndex) => {
                                    const selectedReport = availableChartOptions[selectedIndex];
                                    const displayType = selectedReport?.allowedDisplayTypes[0];
                                    setChartToBuild(selectedReport);
                                    filtersRegistry.setCurrentReportKey(selectedReport.name);
                                    filtersRegistry.setChartSettings(selectedReport);

                                    setCurrentReport({
                                        name:"",
                                        reportType:null,
                                        // `type` is the backend's; `reportKey` is this report's own identity,
                                        // and the two only differ for a report built on another's data.
                                        type: getSourceType(selectedReport.name),
                                        reportKey: selectedReport.name,
                                        displayType: displayType,
                                        // Only a report that offers subtypes carries the field; the others
                                        // never send it and the backend reads a missing subtype as base.
                                        ...(selectedReport?.subtypes?.length && { subtype: reportSubtypeKeys.BASE }),
                                    })
                                    // dispatch(setReport(selectedReport));
                                }}
                                disallowDeselection
                                closeOnSelection
                                options={availableChartOptions.map(option => t(getReportLabelKey(option.name)))}
                                height={32}
                                // variant="darker"
                                selected={availableChartOptions.findIndex(option => option.name === chartToBuild?.name) ?? -1}
                            />
                        </Box>
                        {chartToBuild?.subtypes?.length > 0 && (
                            <Box
                                sx={{
                                    mt:1.5,
                                }}
                            >
                                <Text>
                                    {t("choose_report_subtype")}
                                </Text>
                                <Box
                                    sx={{
                                        mt:0.5,
                                    }}
                                >
                                    <RhSubtypeToggle
                                        currentReport={currentReport}
                                        setCurrentReport={setCurrentReport}
                                        background={theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100]}
                                        sx={{ width:1 }}
                                    />
                                </Box>
                            </Box>
                        )}
                        {currentReport !== null && (
                            <Box
                                sx={{
                                    mt:1.5,
                                }}
                            >
                                <Text>
                                    {t("choose_report_type_type")}
                                </Text>
                                <InputDropdown
                                    onChange={(selectedIndex) => {

                                        let obj = {};
                                        if(reportTypes[selectedIndex] === reportTypeOptionKeys.WITHCHART) {
                                            obj = {
                                                date: {
                                                    type:chartDateOptions.RELATIVE,
                                                    days:7,
                                                    frame:chartDateTimeFrames.ONEDAY,
                                                },
                                                filter: {},
                                                // The dimension a report owns is its pivot column, and on a chart it
                                                // is the category: same dimension, the axis it lands on differs.
                                                // Its values are seeded whole rather than left to be picked, because a
                                                // report about what is missing has to plot the empty buckets too.
                                                category: chartToBuild?.fixedPivotColumn
                                                    ? {
                                                        use: true,
                                                        key: chartToBuild.fixedPivotColumn,
                                                        values: getCategoryValues(chartToBuild.fixedPivotColumn),
                                                    }
                                                    : {
                                                        use: false,
                                                    }
                                            }
                                        } else {
                                            obj = {
                                                date: {
                                                    type:chartDateOptions.DYNAMIC,
                                                    from: format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"),
                                                    to: format(endOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"),
                                                    frame: chartDateTimeFrames.ONEDAY,
                                                },
                                                filter: {},
                                                table: {
                                                    row: null,
                                                    // A report that owns its column dimension gets it here rather than
                                                    // the usual date range, so picking the report is the configuration.
                                                    // Read off chartToBuild, which is set straight from the report that
                                                    // was picked — chartOption is derived from state and can still be a
                                                    // render behind when both dropdowns are used in one go.
                                                    column: chartToBuild?.fixedPivotColumn
                                                        ? {
                                                            type: pivotTableCategoryOptions.COLUMN,
                                                            key: chartToBuild.fixedPivotColumn,
                                                            values: [],
                                                        }
                                                        : {
                                                            type: pivotTableCategoryOptions.DATERANGE
                                                        }
                                                }
                                            }

                                        }

                                        setCurrentReport(prev => ({
                                            ...prev,
                                            ...obj,
                                            reportType:reportTypes[selectedIndex],
                                        }))
                                        // dispatch(setReport(selectedReport)); 
                                    }}
                                    disallowDeselection
                                    closeOnSelection
                                    options={reportTypes}
                                    height={32}
                                    useTranslation
                                    // variant="darker"
                                    selected={reportTypes.findIndex(option => option === currentReport.reportType) ?? -1} 
                                />
                            </Box>

                        )}
                    </Box>

                    {(currentReport != null && currentReport.reportType != null) &&
                        currentReport?.reportType === reportTypeOptionKeys.WITHCHART ? (
                            <>
                                <Box
                                    sx={{
                                        mt:2,
                                        p:3,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        maxWidth:700,
                                        borderRadius:4,
                                        width:1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width:1,
                                        }}
                                    >
                                        <Text>{t("date_range")}</Text>
                                        <Box
                                            sx={{
                                                width:1,
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    display:"flex",
                                                    justifyContent:"center",
                                                    flexDirection:'column',
                                                    borderRadius:5,

                                                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,

                                                }
                                            }}
                                        >
                                            <ToggleButtonGroup
                                                // color={currentReport?.category?.use ? "green" : "red"}
                                                value={currentReport?.date.type}
                                                exclusive
                                                
                                                onChange={(e, newType) => {
                                                    if (!newType) return;

                                                    setCurrentReport(prev => {
                                                        if (newType === chartDateOptions.RELATIVE) {
                                                            return {
                                                                ...prev,
                                                                date: {
                                                                    ...prev.date,
                                                                    type: chartDateOptions.RELATIVE,
                                                                    days: 7,
                                                                    frame: chartDateTimeFrames.ONEDAY,
                                                                },
                                                            };
                                                        }

                                                        if (newType === chartDateOptions.DYNAMIC) {
                                                            const end = new Date();
                                                            const start = new Date();
                                                            start.setDate(end.getDate() - 30);

                                                            const format = d => d.toISOString().slice(0, 10); // YYYY-MM-DD

                                                            return {
                                                                ...prev,
                                                                date: {
                                                                    ...prev.date,
                                                                    type: chartDateOptions.DYNAMIC,
                                                                    from: format(start),
                                                                    to: format(end),
                                                                },
                                                            };
                                                        }

                                                        if (newType === chartDateOptions.ROLLING) {
                                                            return {
                                                                ...prev,
                                                                date: {
                                                                    ...prev.date,
                                                                    type: chartDateOptions.ROLLING,
                                                                    unit: chartRollingUnits.WEEK,
                                                                    frame: rollingUnitToFrame[chartRollingUnits.WEEK],
                                                                    compare: [{ type: "offset", n: 1 }],
                                                                },
                                                            };
                                                        }

                                                        return prev;
                                                    });
                                                }}

                                                aria-label="Active"

                                                className={cx(`${currentReport.category.use}`, {
                                                    'active': currentReport.date.type == chartDateOptions.RELATIVE
                                                })}
                                                sx={{
                                                    width:1,
                                                    background:colors.grey[100],
                                                    padding:0.5,
                                                    borderRadius:5,
                                                    transition:"250ms",
                                                    "&.active" : {
                                                        background: colors.grey[100],
                                                        transition:"250ms",
                                                    },
                                                }}
                                            >
                                                <ToggleButton
                                                    sx={{
                                                        flex:1,
                                                        padding: 0,
                                                        paddingX: 2,
                                                        border: 0,
                                                        lineHeight: "32px",
                                                        textTransform: "unset",
                                                        borderRadius: "32px !important",
                                                        color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], 
                                                        transition: "250ms",
                                                        "&.Mui-selected": {
                                                            background: colors.grey[400], 
                                                            color: "#fff", 
                                                            fontWeight:"bold",
                                                        },
                                                        "&.Mui-selected:hover": {
                                                            background:theme.palette.mode === "dark" ? colors.grey[600]: "#1F293D",
                                                        },
                                                    }}
                                                    value={chartDateOptions.RELATIVE}
                                                >
                                                    <span
                                                        style={{
                                                            flexShrink:0,
                                                        }}
                                                    >
                                                    {t('relative')} 
                                                    </span>
                                                    {currentReport.date.type === chartDateOptions.RELATIVE  ?
                                                        <Box 
                                                            sx={{
                                                                mx:1,
                                                                // mt:1,
                                                                // mb:3,
                                                                width:1,
                                                                display:"flex",
                                                                alignItems:"center",
                                                                justifyContent:"space-between",
                                                                gap:2,
                                                                "@media screen and (max-width: 47.9375em)" : {
                                                                    display:"none",
                                                                }
                                                            }}
                                                        >
                                                            <NumberInput btnSize={"16"}
                                                                hideButtons
                                                                height={24}
                                                                min={1}
                                                                max={366}
                                                                inputSX={nativeSpinnerSuppression}
                                                                value={currentReport.date.days}
                                                                change={(e) => {
                                                                    setCurrentReport(prev => {
                                                                        const days = e;
                                                                        const frame = normalizeFrame(prev.date.frame, days);

                                                                        return {
                                                                            ...prev,
                                                                            date: {
                                                                                ...prev.date,
                                                                                days,
                                                                                frame,
                                                                            },
                                                                        };
                                                                    });


                                                                }}
                                                            />
                                                        </Box>
                                                        :
                                                        <span>&nbsp;...&nbsp;</span>
                                                    }
                                                    {currentReport.date.type === chartDateOptions.RELATIVE  &&
                                                        <Text
                                                            sx={{
                                                                color:theme.palette.mode === "dark" ? colors.txt["primary"] : colors.txt['light'],
                                                                display:"none",
                                                                "@media screen and (max-width: 47.9375em)" : {
                                                                    display:"block",
                                                                }
                                                            }}
                                                        >
                                                            &nbsp;...&nbsp;
                                                        </Text>
                                                    }
                                                    <span
                                                        style={{
                                                            flexShrink:0,
                                                        }}
                                                    >
                                                        {t("in_days")}
                                                    </span>
                                                </ToggleButton>
                                                <ToggleButton
                                                    sx={{
                                                        flex:1,
                                                        padding: 0,
                                                        paddingX: 2,
                                                        border: 0,
                                                        lineHeight: "32px",
                                                        textTransform: "unset",
                                                        borderRadius: "32px !important",
                                                        color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                                                        transition: "250ms",
                                                        "&.Mui-selected": {
                                                            background: colors.grey[400],
                                                            color: theme.palette.mode === "dark" ? colors.txt["primary"] :  colors.txt["white"],
                                                            fontWeight:"bold",
                                                            pr:0.5,
                                                            display:'flex',
                                                            alignItems:'center',
                                                            gap:1.5,
                                                            "@media screen and (max-width: 47.9375em)" : {
                                                                justifyContent:'center',
                                                            }
                                                        },
                                                        "&.Mui-selected:hover": {
                                                            background:theme.palette.mode === "dark" ? colors.grey[600]: "#1F293D",
                                                        },
                                                    }}
                                                    value={chartDateOptions.ROLLING}
                                                >
                                                    {t('current')}
                                                    {currentReport.date.type === chartDateOptions.ROLLING &&
                                                        <Box
                                                            sx={{
                                                                flex:1,
                                                                "@media screen and (max-width: 47.9375em)" : {
                                                                    display:"none",
                                                                }
                                                            }}
                                                        >
                                                            <RhRollingUnitDropDown currentReport={currentReport} setCurrentReport={setCurrentReport} />
                                                        </Box>
                                                    }
                                                </ToggleButton>
                                                <ToggleButton
                                                    sx={{
                                                        flex:1,
                                                        padding: 0,
                                                        paddingX: 2,
                                                        border: 0,
                                                        lineHeight: "32px",
                                                        textTransform: "unset",
                                                        borderRadius: "32px !important",
                                                        color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                                                        // background: currentReport.category.use ? colors.grey[400] : "transparent",
                                                        transition: "250ms",
                                                        "&.Mui-selected": {
                                                            background: colors.grey[400],
                                                            color: theme.palette.mode === "dark" ? colors.txt["primary"] :  colors.txt["white"],
                                                            fontWeight:"bold",
                                                            pr:0.5,
                                                            display:'flex',
                                                            alignItems:'center',
                                                            gap:1.5,
                                                            "@media screen and (max-width: 47.9375em)" : {
                                                                justifyContent:'center',
                                                            }
                                                        },

                                                        "&.Mui-selected:hover": {
                                                            background:theme.palette.mode === "dark" ? colors.grey[600]: "#1F293D",
                                                        },
                                                    }}
                                                    value={chartDateOptions.DYNAMIC}
                                                >
                                                    {t('date_range')}

                                                    {currentReport.date.type === chartDateOptions.DYNAMIC  &&
                                                        <Box
                                                            sx={{
                                                                flex:1,
                                                                "@media screen and (max-width: 47.9375em)" : {
                                                                    display:"none",
                                                                }
                                                            }}
                                                        >
                                                            <ShDateRangePicker
                                                                startDate={currentReport.date.from}
                                                                endDate={currentReport.date.to}
                                                                size={24}
                                                                select={(value) => {
                                                                    setCurrentReport(prev => {
                                                                        const from = value[0].formattedStartDate;
                                                                        const to = value[0].formattedEndDate;

                                                                        const days = calculateDaysBetween(from, to);
                                                                        const frame = normalizeFrame(prev.date.frame, days);

                                                                        return {
                                                                            ...prev,
                                                                            date: {
                                                                                ...prev.date,
                                                                                from,
                                                                                to,
                                                                                frame,
                                                                            },
                                                                        };
                                                                    });
                                                                }}
                                                                explicit
                                                            />
                                                        </Box>
                                                    }
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                            <Box
                                                sx={{
                                                    display:"none",
                                                    "@media screen and (max-width: 47.9375em)" : {
                                                        display:"block"
                                                    }
                                                }}
                                            >
                                                {currentReport.date.type === chartDateOptions.RELATIVE  &&
                                                    <Box 
                                                        sx={{
                                                            p:2,
                                                            // mt:1,
                                                            // mb:3,
                                                            width:1,
                                                            display:"flex",
                                                            alignItems:"center",
                                                            justifyContent:"space-between",
                                                            gap:2,
                                                        }}
                                                    >
                                                        <NumberInput btnSize={"24"}
                                                            height={32}
                                                            min={1}
                                                            max={366}
                                                            value={currentReport.date.days}
                                                            change={(e) => {
                                                                setCurrentReport(prev => {
                                                                    const days = e;
                                                                    const frame = normalizeFrame(prev.date.frame, days);

                                                                    return {
                                                                        ...prev,
                                                                        date: {
                                                                            ...prev.date,
                                                                            days,
                                                                            frame,
                                                                        },
                                                                    };
                                                                });
                                                            }}
                                                            inputSX={{
                                                                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[100],
                                                                ...nativeSpinnerSuppression,
                                                            }}
                                                        />
                                                    </Box>
                                                }
                                                {currentReport.date.type === chartDateOptions.DYNAMIC  &&
                                                    <Box
                                                        sx={{
                                                            width:1,
                                                            p:2,
                                                        }}
                                                    >
                                                        <ShDateRangePicker
                                                            sx={{
                                                                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[100],

                                                            }}
                                                            startDate={currentReport.date.from}
                                                            endDate={currentReport.date.to}
                                                            size={32}
                                                            select={(value) => {
                                                                setCurrentReport(prev => {
                                                                    const from = value[0].formattedStartDate;
                                                                    const to = value[0].formattedEndDate;

                                                                    const days = calculateDaysBetween(from, to);
                                                                    const frame = normalizeFrame(prev.date.frame, days);

                                                                    return {
                                                                        ...prev,
                                                                        date: {
                                                                            ...prev.date,
                                                                            from,
                                                                            to,
                                                                            frame,
                                                                        },
                                                                    };
                                                                });
                                                            }}
                                                            explicit
                                                        />
                                                    </Box>
                                                }
                                                {currentReport.date.type === chartDateOptions.ROLLING &&
                                                    <Box
                                                        sx={{
                                                            width:1,
                                                            p:2,
                                                        }}
                                                    >
                                                        <RhRollingUnitDropDown currentReport={currentReport} setCurrentReport={setCurrentReport} />
                                                    </Box>
                                                }

                                            </Box>
                                        </Box>

                                    </Box>

                                    {(currentReport?.displayType === chartTypes.BAR && currentReport.category.use) && (
                                        // A report that owns its dimension has nothing to choose here: the one it owns
                                        // is the only one, so it is shown as the selection instead of as a list.
                                        (chartOption?.fixedPivotColumn ? [chartOption.fixedPivotColumn] : chartOption?.filters ?? []).map((key, idx) => (
                                            <ButtonBase
                                                disabled={Boolean(chartOption?.fixedPivotColumn)}
                                                className={cx(undefined, {
                                                    'selected': currentReport.category.key === key,
                                                })}
                                                onClick={() => {
                                                    if(currentReport.category.key !== key) {
                                                        setCurrentReport(prev => ({
                                                            ...prev,
                                                            category: {
                                                                ...prev.category,
                                                                key:key,
                                                                values: [], 
                                                            },
                                                        }));
                                                    }
                                                }}
                                                sx={{
                                                    width:1,
                                                    mt:1,
                                                    borderRadius:5,
                                                    px:2,
                                                    pr:0.5,
                                                    height:40,
                                                    display:"flex",
                                                    alignItems:'center',
                                                    justifyContent:"space-between",
                                                    background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                                    "&.selected" : {
                                                        background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                                        borderColor:theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                                    }
                                                }}
                                            >
                                                <Text 
                                                    semibold={currentReport.category?.key === key}
                                                    variant={currentReport.category?.key === key ? theme.palette.mode === "dark" ? "blue" : "primary" : null}
                                                >
                                                    {filtersRegistry.filters[key].name}
                                                </Text>

                                                {(() => {
                                                    if (currentReport.category.key !== key) return null;
                                                    // Nothing to narrow: the report plots every value of the dimension
                                                    // it owns, so a picker here would be a control that does nothing.
                                                    if (chartOption?.fixedPivotColumn) return null;

                                                    const filterConfig = typeof filtersRegistry.filters[key] === "function"
                                                        ? filtersRegistry.filters[key]()
                                                        : filtersRegistry.filters[key];
                                                    return filterConfig?.componentType === reportFilterComponentTypes.DROPDOWN ? (
                                                        <CatogoryDropdown
                                                            fKey={key}
                                                            category={currentReport.category}
                                                            setCurrentReport={setCurrentReport}
                                                        />
                                                    ) :  filterConfig?.componentType === reportFilterComponentTypes.COUNTRIES ? (
                                                        <CategoryCountryDropdown
                                                            fKey={key}
                                                            category={currentReport.category}
                                                            setCurrentReport={setCurrentReport}
                                                        />
                                                    ) : filterConfig?.componentType === reportFilterComponentTypes.SEARCHQUERYDROPDOWN ? (
                                                        <CategorySearchQueryDropdown
                                                            fKey={key}
                                                            category={currentReport.category}
                                                            setCurrentReport={setCurrentReport}
                                                        />
                                                    ) :  filterConfig?.componentType === reportFilterComponentTypes.BOOLEAN ?
                                                        <RhCategoryBoolean
                                                            fKey={key}
                                                            category={currentReport.category}
                                                            setCurrentReport={setCurrentReport}
                                                        />
                                                    :
                                                    null
                                                    ;
                                                })()}
                                            </ButtonBase>
                                        )))
                                    }
                                    <Box
                                        sx={{
                                            mt:1.5,
                                        }}
                                    >
                                        <Text>
                                            {t("choose_filters")}
                                        </Text>
                                        <RhFilter
                                            wrapFilters
                                            currentReport={currentReport}
                                            setCurrentReport={setCurrentReport}
                                        />
                                    </Box>
                                </Box>
                                {currentReport?.displayType &&
                                    <Shbutton

                                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                        variant="contained"
                                        size="32"
                                        block
                                        sx={{
                                            mt:3,
                                        }}
                                        onClick={() => {
                                            setChartReadyToPreview(true);
                                            // handleRegenBi();
                                        }}
                                    >
                                        {t("view")}
                                    </Shbutton>
                                }
                            </>
                        ) : currentReport?.reportType === reportTypeOptionKeys.PIVOTTABLE ?
                        (
                            <>
                                <Box
                                    sx={{
                                        mt:2,
                                        p:3,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        maxWidth:700,
                                        borderRadius:4,
                                        width:1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width:1,
                                        }}
                                    >
                                        <Text>{t("date_range")}</Text>
                                        <RhDateSelector
                                            currentReport={currentReport}
                                            setCurrentReport={setCurrentReport}
                                        />

                                    </Box>

                                    <Box
                                        sx={{
                                            mt:1.5,
                                        }}
                                    >
                                        <Text>
                                            {t("choose_filters")}
                                        </Text>
                                        <RhFilter
                                            wrapFilters
                                            currentReport={currentReport}
                                            setCurrentReport={setCurrentReport}
                                        />
                                    </Box>
                                </Box>
                                
                                <Box
                                    sx={{
                                        mt:2,
                                        p:3,
                                        background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                                        maxWidth:700,
                                        borderRadius:4,
                                        width:1,
                                        display:"flex",
                                        gap:2,
                                    }}
                                >

                                    <Box
                                        sx={{
                                            flex:1,
                                            mt:6,
                                        }}
                                    >
                                        <RhPivotTableRow
                                            currentReport={currentReport}
                                            setCurrentReport={setCurrentReport}
                                            chartOption={chartOption}
                                            error={pivotRowError}
                                        />   
                                    </Box>
                                    <Box
                                        sx={{
                                            flex:2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width:1,
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    display:"flex",
                                                    justifyContent:"center",
                                                    flexDirection:'column',
                                                    borderRadius:5,

                                                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                    border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                                    boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,

                                                }
                                            }}
                                        >
                                            <ToggleButtonGroup
                                                value={currentReport?.table.column.type}
                                                exclusive
                                                
                                                onChange={(e, newType) => {
                                                    if (!newType) return;

                                                    setCurrentReport(prev => {
                                                        if(newType === pivotTableCategoryOptions.COLUMN) {
                                                            return {
                                                                ...prev,
                                                                table: {
                                                                    row:prev.table.row,
                                                                    column: {
                                                                        type: pivotTableCategoryOptions.COLUMN,
                                                                        key:null,
                                                                        values:null,
                                                                    }
                                                                }
                                                            }
                                                        }
                                                        if(newType === pivotTableCategoryOptions.DATERANGE) {
                                                            return {
                                                                ...prev,
                                                                table: {
                                                                    row:prev.table.row,
                                                                    column: {
                                                                        type: pivotTableCategoryOptions.DATERANGE,
                                                                    }
                                                                }
                                                            }

                                                        }
                                                        throw new Error("Unrecognized pivot table type");
                                                    });
                                                }}

                                                aria-label="Active"

                                                // className={cx(`${currentReport.table.column.type}`, {
                                                //     'active': currentReport.date.type == chartDateOptions.RELATIVE 
                                                // })}
                                                sx={{
                                                    width:1,
                                                    background:colors.grey[100],
                                                    padding:0.5,
                                                    borderRadius:5,
                                                    transition:"250ms",
                                                    // "&.active" : {
                                                    //     background: colors.grey[100],
                                                    //     transition:"250ms",
                                                    // },
                                                }}
                                            >
                                                <ToggleButton
                                                    sx={{
                                                        flex:1,
                                                        padding: 0,
                                                        paddingX: 2,
                                                        border: 0,
                                                        lineHeight: "32px",
                                                        textTransform: "unset",
                                                        borderRadius: "32px !important",
                                                        color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], 
                                                        transition: "250ms",
                                                        "&.Mui-selected": {
                                                            background: colors.grey[400], 
                                                            color: "#fff", 
                                                            fontWeight:"bold",
                                                        },
                                                        "&.Mui-selected:hover": {
                                                            background:theme.palette.mode === "dark" ? colors.grey[600]: "#1F293D",
                                                        },
                                                    }}
                                                    value={pivotTableCategoryOptions.DATERANGE}
                                                    // A report that owns its column dimension cannot be switched to a
                                                    // date range: its columns are what it measures.
                                                    disabled={Boolean(chartOption?.fixedPivotColumn)}
                                                >
                                                    <span
                                                        style={{
                                                            flexShrink:0,
                                                        }}
                                                    >
                                                    {t('date_range')} 
                                                    </span>
                                                </ToggleButton>
                                                <ToggleButton
                                                    sx={{
                                                        flex:3,
                                                        padding: 0,
                                                        paddingX: 2,
                                                        border: 0,
                                                        lineHeight: "32px",
                                                        textTransform: "unset",
                                                        borderRadius: "32px !important",
                                                        color: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400], 
                                                        // background: currentReport.category.use ? colors.grey[400] : "transparent", 
                                                        transition: "250ms",
                                                        "&.Mui-selected": {
                                                            background: colors.grey[400], 
                                                            color: theme.palette.mode === "dark" ? colors.txt["primary"] :  colors.txt["white"], 
                                                            fontWeight:"bold",
                                                            pr:0.5,
                                                            display:'flex',
                                                            alignItems:'center',
                                                            gap:1.5,
                                                            "@media screen and (max-width: 47.9375em)" : {
                                                                justifyContent:'center',
                                                            }
                                                        },
                                                        
                                                        "&.Mui-selected:hover": {
                                                            background:theme.palette.mode === "dark" ? colors.grey[600]: "#1F293D",
                                                        },
                                                    }}
                                                    value={pivotTableCategoryOptions.COLUMN}
                                                >
                                                    {t('column')}

                                                    {currentReport.table.column.type === pivotTableCategoryOptions.COLUMN  &&
                                                        <Box
                                                            sx={{
                                                                flex:1,
                                                                "@media screen and (max-width: 47.9375em)" : {
                                                                    display:"none",
                                                                }
                                                            }}
                                                        >
                                                            <RhPivotTableColumn
                                                                key={currentReport.table.row + " " + currentReport.table.column.key}
                                                                chartOption={chartOption}
                                                                setCurrentReport={setCurrentReport}
                                                                currentReport={currentReport}
                                                            />
                                                        </Box>
                                                    }
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                            <Box
                                                sx={{
                                                    display:"none",
                                                    "@media screen and (max-width: 47.9375em)" : {
                                                        display:"block"
                                                    }
                                                }}
                                            >
                                                {currentReport.table.column.type === pivotTableCategoryOptions.COLUMN  &&
                                                    <Box
                                                        sx={{
                                                            width:1,
                                                            p:2,
                                                        }}
                                                    >
                                                        <RhPivotTableColumn
                                                            key={currentReport.table.row + " " + currentReport.table.column.key}
                                                            chartOption={chartOption}
                                                            setCurrentReport={setCurrentReport}
                                                            currentReport={currentReport}
                                                        />
                                                        
                                                    </Box>
                                                }

                                            </Box>
                                        </Box>
                                        
                                        {/* Grid */}
                                        <Box
                                            sx={{
                                                height:200,
                                                border:`2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                                mt:1.5,
                                                borderRadius:3,
                                                display:"flex",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    flex:1,

                                                    borderRight:`2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                                    display:"flex",
                                                    flexDirection:'column',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                                    }}
                                                ></Box>
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                                    }}
                                                ></Box>
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                    }}
                                                ></Box>

                                            </Box>
                                            <Box
                                                sx={{
                                                    flex:1,

                                                    borderRight:`2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                                    display:"flex",
                                                    flexDirection:'column',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                                    }}
                                                ></Box>
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                                    }}
                                                ></Box>
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                    }}
                                                ></Box>

                                            </Box>
                                            <Box
                                                sx={{
                                                    flex:1,

                                                    display:"flex",
                                                    flexDirection:'column',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                                    }}
                                                ></Box>
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200]}`,
                                                    }}
                                                ></Box>
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                    }}
                                                ></Box>

                                            </Box>
                                        </Box>

                                    </Box>
                                </Box>
                                <Shbutton

                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    variant="contained"
                                    size="32"
                                    block
                                    sx={{
                                        mt:3,
                                    }}
                                    onClick={() => {
                                        if(!currentReport.table?.row) {
                                            setPivotRowError(t("pivot_row_required"));
                                            return;
                                        }
                                        setPivotRowError("");
                                        setChartReadyToPreview(true);
                                    }}
                                >
                                    {t("view")}
                                </Shbutton>
                            </>
                        )
                        :
                        null
                    }
                </Box>
                :

                currentReport.reportType === reportTypeOptionKeys.WITHCHART ?
                    <>
                        <RhChart
                            isError={isError}
                            isLoading={chartLoading}
                            fetchFetching={chartLoading}
                            chartInfo={chartInfo}
                            currentReport={currentReport}
                            setCurrentReport={setCurrentReport}
                            defaultNameEdit
                            refetch={refetch}
                            nameError={nameError}
                            setNameError={setNameError}
                        />
                        <RhDataTable
                            chartInfo={chartInfo}
                            currentReport={currentReport}
                            data={data}
                            page={page}
                            setPageSize={setPageSize}
                            setPage={setPage}
                            isSuccess={isSuccess}
                            isError={isError}
                            isLoading={isLoading || fetchFetching}
                            handleSaveReport={handleSaveReport}
                            onSortChange={handleSortChange}
                            selectedActions={
                                <Box
                                    sx={{
                                        height:1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height:1,
                                            background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                            px:1,
                                            borderTopRightRadius:28,
                                            borderBottomRightRadius:28,
                                            display:'flex',
                                            alignItems:'center',
                                            justifyContent:'center',
                                            gap:1,
                                            "@media screen and (max-width: 47.9375em)" : {
                                                display:'none'
                                            }
                                        }}
                                    >

                                        <Shbutton
                                            onClick={handleSaveReport}
                                            isLoading={reportSaving}
                                            variant="contained"
                                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                            sx={{
                                                whiteSpace:"nowrap",
                                                px:3,
                                                minWidth:"unset",
                                            }}
                                        >
                                            {t("save_report")}
                                        </Shbutton>
                                    </Box>
                                    <ShRhDataGridSelectedActions
                                        sx={{
                                            height:48,
                                            borderRadius:7,
                                            display:"none",
                                            px:0.5,
                                            alignItems:'center',
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            "@media screen and (max-width: 47.9375em)" : {
                                                display:'flex'
                                            }

                                        }}
                                    >

                                        <DropdownButton
                                            actionIconColor={theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}
                                            onClick={handleSaveReport} 
                                            icon={<SaveOutlinedIcon/>} 
                                            text={t("save_report")}
                                        />
                                    </ShRhDataGridSelectedActions>
                                </Box>
                            }
                        />
                    </>
                :
                <>
                    <RhPivotHeader
                        isError={isError}
                        isLoading={chartLoading}
                        fetchFetching={chartLoading}
                        chartInfo={chartInfo}
                        currentReport={currentReport}
                        setCurrentReport={setCurrentReport}
                        defaultNameEdit
                        refetch={refetch}
                        nameError={nameError}
                        setNameError={setNameError}
                        heatmapMode={heatmapMode}
                    />
                    <RhPivotTable
                        data={biData?.pivot}
                        chartOption={chartOption}
                        currentReport={currentReport}
                        setCurrentReport={setCurrentReport}
                        isError={isError}
                        heatmapMode={heatmapMode}
                        actions={
                            <Box
                                className={cx(undefined,{
                                    'collapsed':collapsed,
                                })}
                                sx={{
                                    position:"fixed",
                                    bottom:16,
                                    zIndex:theme.zIndex.appBar, // below modal z-index so an open filter popover closes (and commits) before save is clicked
                                    left: "calc(50% + 342px)",
                                    transform:"translateX(calc(-50% - 171px))",
                                    background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                    px:1,
                                    borderRadius:7,
                                    display:'flex',
                                    alignItems:'center',
                                    justifyContent:'center',
                                    gap:1,
                                    "@media screen and (max-width: 47.9375em)" : {
                                        display:'none'
                                    },
                                    "&.collapsed": {
                                        left:"50%" ,
                                        transform: "translateX(-50%)" ,
                                        transition:"500ms",

                                    },
                                }}
                            >

                                <Shbutton
                                    onClick={handleSaveReport}
                                    isLoading={reportSaving}
                                    variant="contained"
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                    sx={{
                                        whiteSpace:"nowrap",
                                        px:3,
                                        minWidth:"unset",
                                    }}
                                >
                                    {t("save_report")}
                                </Shbutton>
                                <RhPivotExportButton
                                    data={biData?.pivot}
                                    currentReport={currentReport}
                                    chartOption={chartOption}
                                />
                            </Box>
                        }
                    />
                </>
            }

        
        </Box>
    );
};

export default ReportDetailPageBuilder;
